import type { StructuredMeasurements } from '@restDataAccess';
import type { MeasurementRange } from '@scaleaq/sitecontrol-lib';

import { ChartAggregationResolution } from '@/components/chart/types.ts';
import { fromApiBucketDay } from '@/utils/apiDateTime.ts';

/** Daily and weekly buckets start at midnight of an API (Oslo) day. */
export const hasApiDayBuckets = (resolution: ChartAggregationResolution | undefined) =>
  resolution === ChartAggregationResolution.DAILY ||
  resolution === ChartAggregationResolution.WEEKLY;

type Measurements = StructuredMeasurements<MeasurementRange>;

type ChartMeasurementsParams = {
  data: Measurements | undefined;
  normalizeTimeSeries: string[];
  resolution: ChartAggregationResolution | undefined;
};

type ChartMeasurements = {
  allMeasurements: Measurements | undefined;
  /** Normalized series are present, so the x-axis snaps to whole days. */
  isTimeSeriesNormalized: boolean;
};

/**
 * Places measurements where the chart draws them.
 *
 * Daily and weekly buckets all move to local midnight of their API day, so none drift a day in
 * browser zones behind Oslo; in Oslo their instants stay the same. `normalizeTimeSeries` series are
 * moved at any resolution, and only they snap the x-axis to whole days
 * (`isTimeSeriesNormalized`); the live axis keeps the other moved buckets in view by itself.
 */
export const getChartMeasurements = ({
  data,
  normalizeTimeSeries,
  resolution,
}: ChartMeasurementsParams): ChartMeasurements => {
  if (!data) return { allMeasurements: data, isTimeSeriesNormalized: false };

  const keysToNormalize = normalizeTimeSeries.filter(key => key in data);
  const keysToMove = hasApiDayBuckets(resolution) ? Object.keys(data) : keysToNormalize;
  if (keysToMove.length === 0) return { allMeasurements: data, isTimeSeriesNormalized: false };

  // Series share their bucket timestamps, so each distinct one is converted once.
  const movedTimestamps = new Map<string, string>();
  const toApiDayIso = (timestamp: string) => {
    if (!movedTimestamps.has(timestamp)) {
      const day = fromApiBucketDay(timestamp);
      movedTimestamps.set(timestamp, (day.isValid && day.toISO()) || timestamp);
    }
    return movedTimestamps.get(timestamp)!;
  };

  return {
    allMeasurements: {
      ...data,
      ...Object.fromEntries(
        keysToMove.map(key => [
          key,
          Object.fromEntries(
            Object.entries(data[key]).map(([inventoryId, readings]) => [
              inventoryId,
              readings.map(reading => ({ ...reading, timestamp: toApiDayIso(reading.timestamp) })),
            ])
          ),
        ])
      ),
    },
    isTimeSeriesNormalized: keysToNormalize.length > 0,
  };
};
